import { useTranslation } from "react-i18next";

import type {
  CcCorrectionStatus,
  CcEscalation,
  CcPauseReason,
  CcReadyGate,
  CcRunState,
} from "@/api/code-companion";
import { useCcCorrections, useCcEscalations, useCcReadyGate } from "@/hooks/useCodeCompanion";
import { CC_WARN_INK } from "@/lib/cc-trace-tokens";
import { tp } from "@/lib/plural";
import { pauseMessage } from "@/lib/pause-reason";

/**
 * A fila do que espera decisão de uma pessoa (S-064 onda 7).
 *
 * O protótipo trata isto como painel de primeira classe, ao lado da matriz, e com razão:
 * num run autônomo o que trava é sempre humano, e até aqui cada motivo morava numa tela
 * diferente — a pausa no painel do run, a escalada dentro da trilha de correção, a
 * pendência do gate num terceiro bloco. Quem conduz precisava caçar.
 *
 * **Uma fila, quatro origens.** Escalação aberta, correção escalada, run pausado e gate
 * bloqueado são a mesma pergunta para quem olha: "o que depende de mim agora?".
 *
 * **Ordem por urgência, não por origem.** O run pausado vem primeiro porque é o único
 * que tem trabalho parado atrás dele; o gate vem por último porque bloqueia a promoção,
 * não a execução.
 */

export type NeedKind = "paused" | "escalation" | "correction" | "gate";

export interface NeedItem {
  kind: NeedKind;
  /** Selo curto do protótipo: duas letras dentro do quadrado. */
  chip: string;
  title: string;
  body: string;
  meta?: string;
}

/** Peso de urgência. Menor vem primeiro. */
const PESO: Record<NeedKind, number> = { paused: 0, escalation: 1, correction: 2, gate: 3 };

/**
 * Junta as quatro origens numa fila só.
 *
 * Função pura de propósito: é a regra que decide o que "precisa de gente", e ela tem de
 * ser testável sem render. Recebe texto já traduzido pelo chamador — a tradução é da
 * tela, a decisão é daqui.
 */
export function collectNeeds(
  input: {
    run?: CcRunState | null;
    escalations?: CcEscalation[];
    corrections?: CcCorrectionStatus;
    gate?: CcReadyGate;
  },
  text: {
    paused: (reason: string) => { title: string; body: string };
    escalation: (e: CcEscalation) => { title: string; body: string };
    correction: (obj: string, n: number) => { title: string; body: string };
    gate: (n: number) => { title: string; body: string };
  },
): NeedItem[] {
  const itens: NeedItem[] = [];

  // Run pausado: é o único caso com trabalho parado atrás dele.
  if (input.run?.status === "paused") {
    const reason = input.run.pause_reason ?? "manual";
    const { title, body } = text.paused(reason);
    itens.push({ kind: "paused", chip: "||", title, body, meta: reason });
  }

  for (const e of input.escalations ?? []) {
    if (!e.open) continue;
    const { title, body } = text.escalation(e);
    itens.push({ kind: "escalation", chip: "?", title, body, meta: e.object_name ?? undefined });
  }

  // Correção escalada: o laço desistiu, ou o teto de tentativas estourou.
  for (const o of input.corrections?.objects ?? []) {
    if (!o.escalated) continue;
    const { title, body } = text.correction(o.object_name, o.attempts);
    itens.push({
      kind: "correction",
      chip: "!",
      title,
      body,
      meta: o.last_error ?? undefined,
    });
  }

  const faltando = input.gate?.missing?.length ?? 0;
  if (input.gate && !input.gate.ok && faltando > 0) {
    const { title, body } = text.gate(faltando);
    itens.push({ kind: "gate", chip: "G", title, body });
  }

  return itens.sort((a, b) => PESO[a.kind] - PESO[b.kind]);
}

/**
 * Quantos itens a fila teria, sem precisar do texto.
 *
 * A Trace usa isso pra decidir onde o painel entra: com item, ele sobe pra cima da
 * matriz e ocupa a largura toda; sem item, fica no par de baixo. Nove blocos de
 * mesmo peso numa coluna so faziam justamente o que exige acao ficar abaixo da
 * dobra.
 *
 * Passa texto vazio de proposito: a contagem e a mesma regra do painel, e duplicar
 * a regra aqui seria a segunda fonte que diverge na primeira mudanca.
 */
export function countNeeds(input: Parameters<typeof collectNeeds>[0]): number {
  const empty = { title: "", body: "" };
  return collectNeeds(input, {
    paused: () => empty,
    escalation: () => empty,
    correction: () => empty,
    gate: () => empty,
  }).length;
}

const CHIP_TOM: Record<NeedKind, { bg: string; fg: string }> = {
  paused: { bg: "rgba(242,115,37,.12)", fg: CC_WARN_INK },
  escalation: { bg: "rgba(255,200,61,.18)", fg: "#8a6100" },
  correction: { bg: "rgba(212,53,60,.10)", fg: "#b02a31" },
  gate: { bg: "var(--exed-ink-50)", fg: "var(--exed-ink-500)" },
};

export default function NeedsAHuman({
  projectId,
  packageId,
  changeSetId,
  run,
}: {
  projectId: string;
  packageId: number;
  changeSetId?: number | null;
  run?: CcRunState | null;
}) {
  const { t } = useTranslation();
  const escalations = useCcEscalations(projectId, run?.run_id);
  const corrections = useCcCorrections(projectId, changeSetId ?? undefined);
  const gate = useCcReadyGate(projectId, packageId);


  const itens = collectNeeds(
    {
      run,
      escalations: escalations.data?.items,
      corrections: corrections.data,
      gate: gate.data,
    },
    {
      paused: (reason) => ({
        title: t("The run is paused"),
        body: pauseMessage(t, reason as CcPauseReason),
      }),
      escalation: (e) => ({
        title: t("The run asked you something"),
        body:
          (typeof e.question?.text === "string" && e.question.text) ||
          t("Open the run panel to answer."),
      }),
      correction: (obj, n) => ({
        title: t("{{object}} needs a person", { object: obj }),
        // A contagem é do change set e soma execuções anteriores (#603): sem o rótulo,
        // "7 tentativas" num run que fez uma sugere que o modelo insistiu sete vezes
        // agora, e a pessoa decide escopo com a impressão errada.
        body: tp(
          t,
          n,
          "The auto-fix gave up after 1 attempt in this change set and escalated.",
          "The auto-fix gave up after {{n}} attempts in this change set and escalated.",
        ),
      }),
      gate: (n) => ({
        title: t("Promotion is blocked"),
        body: tp(t, n, "1 item still missing for ready for deploy.", "{{n}} items still missing for ready for deploy."),
      }),
    },
  );

  return (
    <div
      data-testid="needs-a-human"
      className="overflow-hidden rounded-xl bg-card"
      style={{
        border: "1px solid var(--exed-card-border)",
        boxShadow: "0 1px 2px rgba(8,37,76,.04)",
      }}
    >
      <div
        className="flex items-center gap-2.5 px-4 py-3"
        style={{ borderBottom: "1px solid var(--exed-ink-50)" }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CC_WARN_INK} strokeWidth="2" strokeLinecap="round">
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <path d="M12 9v4M12 17h.01" />
        </svg>
        <span style={{ fontFamily: "var(--font-brand)", fontWeight: 700, fontSize: "14.5px" }}>
          {t("Needs a human")}
        </span>
        <span
          data-testid="needs-count"
          className="rounded-full px-1.5 py-0.5"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: ".08em",
            background: "rgba(242,115,37,.12)",
            color: CC_WARN_INK,
            border: "1px solid rgba(242,115,37,.3)",
          }}
        >
          {itens.length}
        </span>
      </div>

      {itens.length === 0 ? (
        // Fila vazia é o estado bom, e dizê-lo é melhor que uma caixa vazia que parece
        // carregamento travado.
        <div className="px-4 py-4 text-[11px] text-muted-foreground">
          {t("Nothing is waiting on you.")}
        </div>
      ) : (
        <ul>
          {itens.map((n, i) => (
            <li
              key={`${n.kind}-${i}`}
              className="flex items-start gap-3 px-4 py-3"
              style={{ borderBottom: "1px solid var(--exed-ink-50)" }}
            >
              <span
                className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-lg"
                style={{
                  background: CHIP_TOM[n.kind].bg,
                  color: CHIP_TOM[n.kind].fg,
                  fontFamily: "var(--font-mono)",
                  fontSize: "10px",
                  fontWeight: 700,
                }}
              >
                {n.chip}
              </span>
              <div className="min-w-0 flex-1">
                <div style={{ fontSize: "13px", fontWeight: 600 }}>{n.title}</div>
                <div
                  className="mt-0.5 break-words"
                  style={{ fontSize: "11.5px", color: "var(--exed-ink-500)", lineHeight: 1.45 }}
                >
                  {n.body}
                </div>
                {n.meta && (
                  <div
                    // O `meta` recebe o erro do SAP, que vem como token longo sem
                    // espaço (nome de classe, URI ADT) e estoura a coluna sem isto.
                    className="mt-1 break-words"
                    title={n.meta}
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "9.5px",
                      color: "var(--exed-ink-400)",
                      letterSpacing: ".04em",
                    }}
                  >
                    {n.meta}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
